import * as React from "react";
import type { TraceSpan } from "@zworks/console-contracts";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Cpu, Sparkles, Wrench, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { buildSpanHierarchy, flattenVisibleHierarchy, formatDurationMs } from "./trace-types";

interface TraceWaterfallProps {
  spans: TraceSpan[];
  selectedSpanId: string | null;
  onSelectSpan: (span: TraceSpan) => void;
  totalDurationMs: number;
}

export function TraceWaterfall({
  spans,
  selectedSpanId,
  onSelectSpan,
  totalDurationMs,
}: TraceWaterfallProps) {
  const [collapsedIds, setCollapsedIds] = React.useState<Set<string>>(new Set());

  // Find minimum start time for relative timeline bar alignment
  const minStartTime = React.useMemo(() => {
    if (spans.length === 0) return 0;
    return Math.min(...spans.map((s) => new Date(s.startedAt).getTime()));
  }, [spans]);

  const maxDuration = Math.max(totalDurationMs, ...spans.map((s) => s.durationMs), 100);

  const tree = React.useMemo(() => buildSpanHierarchy(spans), [spans]);
  const visibleRows = React.useMemo(
    () => flattenVisibleHierarchy(tree, collapsedIds),
    [tree, collapsedIds],
  );

  const toggleCollapse = (spanId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(spanId)) {
        next.delete(spanId);
      } else {
        next.add(spanId);
      }
      return next;
    });
  };

  const collapseAll = () => {
    const allParentIds = new Set<string>();
    for (const span of spans) {
      if (spans.some((s) => s.parentSpanId === span.spanId)) {
        allParentIds.add(span.spanId);
      }
    }
    setCollapsedIds(allParentIds);
  };

  const expandAll = () => {
    setCollapsedIds(new Set());
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-border/70 bg-card">
      {/* Waterfall Controls Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-border/70 bg-muted/20 px-3 py-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-foreground">Execution Hierarchy</span>
          <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground">
            {spans.length} spans
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={expandAll}
          >
            Expand all
          </Button>
          <span className="text-border">|</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={collapseAll}
          >
            Collapse all
          </Button>
        </div>
      </div>

      {/* Waterfall Grid Header with Time Axis */}
      <div className="grid grid-cols-[minmax(240px,1.2fr)_minmax(180px,1fr)] border-b border-border/60 bg-muted/10 px-3 py-1.5 font-mono text-xs text-muted-foreground">
        <div>Span Name & Operation</div>
        <div className="relative flex justify-between pr-2">
          <span>0 ms</span>
          <span>{formatDurationMs(Math.round(maxDuration * 0.33))}</span>
          <span>{formatDurationMs(Math.round(maxDuration * 0.66))}</span>
          <span>{formatDurationMs(maxDuration)}</span>
        </div>
      </div>

      {/* Scrollable Tree Rows */}
      <div className="flex-1 overflow-y-auto divide-y divide-border/40">
        {visibleRows.map((item) => {
          const { span, depth, hasChildren } = item;
          const isSelected = selectedSpanId === span.spanId;
          const isCollapsed = collapsedIds.has(span.spanId);
          const failed =
            span.status.toLowerCase() === "error" || Boolean(span.attributes.errorMessage);

          // Relative timing computation
          const spanStartTime = new Date(span.startedAt).getTime();
          const startOffsetMs = Math.max(0, spanStartTime - minStartTime);
          const leftPercent = Math.min(100, Math.max(0, (startOffsetMs / maxDuration) * 100));
          const widthPercent = Math.max(
            1.5,
            Math.min(100 - leftPercent, (span.durationMs / maxDuration) * 100),
          );

          const llm = span.attributes.llm as
            { model?: string; usage?: { totalTokens?: number }; costMicroUSD?: number } | undefined;
          const tool = span.attributes.tool as { toolName?: string } | undefined;

          return (
            <div
              key={span.spanId}
              role="button"
              tabIndex={0}
              onClick={() => onSelectSpan(span)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectSpan(span);
                }
              }}
              className={cn(
                "group grid grid-cols-[minmax(240px,1.2fr)_minmax(180px,1fr)] items-center px-3 py-2 cursor-pointer transition-colors text-xs",
                isSelected
                  ? "bg-sidebar-accent font-medium text-foreground before:absolute before:left-0 before:h-5 before:w-[3px] before:bg-chart"
                  : "hover:bg-accent/50 text-foreground/80",
              )}
            >
              {/* Left Column: Indented Tree with icon & labels */}
              <div
                className="flex items-center gap-1.5 overflow-hidden pr-3"
                style={{ paddingLeft: `${depth * 14}px` }}
              >
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={(e) => toggleCollapse(span.spanId, e)}
                    aria-label={isCollapsed ? "Expand row" : "Collapse row"}
                    className="size-4 shrink-0 rounded text-muted-foreground hover:bg-muted hover:text-foreground flex items-center justify-center"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="size-3" aria-hidden />
                    ) : (
                      <ChevronDown className="size-3" aria-hidden />
                    )}
                  </button>
                ) : (
                  <span className="size-4 shrink-0" />
                )}

                {span.kind === "llm" || llm ? (
                  <Sparkles className="size-3.5 shrink-0 text-chart" aria-hidden />
                ) : span.kind === "tool" || tool ? (
                  <Wrench className="size-3.5 shrink-0 text-sky-400" aria-hidden />
                ) : (
                  <Cpu className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                )}

                <span className="truncate font-mono font-medium text-xs text-foreground">
                  {span.name}
                </span>

                {failed && <XCircle className="size-3.5 shrink-0 text-danger" aria-hidden />}

                {llm?.model && (
                  <span className="hidden xl:inline rounded bg-chart/10 border border-chart/20 px-1 py-0.2 font-mono text-xs text-chart shrink-0">
                    {llm.model}
                  </span>
                )}
                {tool?.toolName && (
                  <span className="hidden xl:inline rounded bg-sky-500/10 border border-sky-500/20 px-1 py-0.2 font-mono text-xs text-sky-400 shrink-0">
                    {tool.toolName}
                  </span>
                )}
              </div>

              {/* Right Column: Interactive Gantt Waterfall Bar */}
              <div className="relative h-6 flex items-center pr-2">
                {/* Background track guide */}
                <div className="absolute inset-x-0 h-1 rounded bg-muted/40" />

                {/* Execution bar */}
                <div
                  className={cn(
                    "absolute h-3.5 rounded shadow-2xs transition-all duration-150 flex items-center justify-end px-1.5",
                    failed
                      ? "bg-bad text-white"
                      : span.kind === "llm" || llm
                        ? "bg-purple-500/80 text-white dark:bg-purple-400 dark:text-black"
                        : span.kind === "tool" || tool
                          ? "bg-sky-500/80 text-white dark:bg-sky-400 dark:text-black"
                          : "bg-chart text-white dark:bg-lime dark:text-black",
                  )}
                  style={{
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`,
                  }}
                  title={`${span.name}: ${formatDurationMs(span.durationMs)}`}
                >
                  {widthPercent > 12 && (
                    <span className="font-mono text-xs font-bold leading-none select-none">
                      {formatDurationMs(span.durationMs)}
                    </span>
                  )}
                </div>

                {/* Duration text outside bar if bar is too narrow */}
                {widthPercent <= 12 && (
                  <span
                    className="absolute font-mono text-xs text-muted-foreground select-none"
                    style={{ left: `calc(${leftPercent + widthPercent}% + 4px)` }}
                  >
                    {formatDurationMs(span.durationMs)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
